import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Collapse,
  Fab,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Timer as TimerIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';

const REST_DURATIONS = [
  { label: '1 мин', seconds: 60 },
  { label: '1:30', seconds: 90 },
  { label: '2 мин', seconds: 120 },
  { label: '3 мин', seconds: 180 },
];

const RestTimer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expanded, setExpanded] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifiedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stopTimer = useCallback(() => {
    clearTimer();
    setRunning(false);
    setPaused(false);
    setRemaining(0);
    setTotalSeconds(0);
  }, [clearTimer]);

  const showNotification = useCallback(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;

    if ('Notification' in window && Notification.permission === 'granted') {
      const n = new Notification('Почивката свърши!', {
        body: 'Време е за следващия сет.',
        icon: '💪',
        requireInteraction: false,
      });
      setTimeout(() => n.close(), 5000);
    }

    if ('vibrate' in navigator) {
      navigator.vibrate([300, 100, 300]);
    }
  }, []);

  const requestPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const startTimer = useCallback((seconds: number) => {
    requestPermission();
    notifiedRef.current = false;
    setTotalSeconds(seconds);
    setRemaining(seconds);
    setRunning(true);
    setPaused(false);
    setExpanded(true);
  }, [requestPermission]);

  const pauseTimer = useCallback(() => {
    clearTimer();
    setPaused(true);
  }, [clearTimer]);

  const resumeTimer = useCallback(() => {
    setPaused(false);
  }, []);

  useEffect(() => {
    if (!running || paused) return;

    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearTimer();
          setRunning(false);
          showNotification();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return clearTimer;
  }, [running, paused, clearTimer, showNotification]);

  useEffect(() => {
    return clearTimer;
  }, [clearTimer]);

  const formatTime = (s: number) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = totalSeconds > 0 ? ((totalSeconds - remaining) / totalSeconds) * 100 : 0;

  const finished = totalSeconds > 0 && remaining === 0 && !running;

  const handleFinishedClick = useCallback(() => {
    setTotalSeconds(0);
    setExpanded(false);
  }, []);

  useEffect(() => {
    if (finished) {
      const timeout = setTimeout(() => {
        setTotalSeconds(0);
        setExpanded(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [finished]);

  const isActive = running || finished;

  const circleSize = isMobile ? 110 : 140;

  return (
    <Box sx={{ my: 2 }}>
      {/* Floating start button - shown when timer is idle and panel is collapsed */}
      {!isActive && !expanded && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Fab
            variant="extended"
            color="primary"
            onClick={() => setExpanded(true)}
            sx={{
              px: 3,
              py: 1,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: 3,
              boxShadow: 3,
            }}
          >
            <TimerIcon sx={{ mr: 1 }} />
            Почивка
          </Fab>
        </Box>
      )}

      {/* Expanded panel with duration buttons OR running timer */}
      <Collapse in={expanded || isActive}>
        <Box
          sx={{
            bgcolor: finished ? 'success.main' : 'background.paper',
            borderRadius: 3,
            boxShadow: 3,
            overflow: 'hidden',
            transition: 'background-color 0.3s ease',
            maxWidth: { sm: 480 },
            mx: 'auto',
          }}
        >
          {/* Progress bar at top when running */}
          {running && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 1s linear',
                },
              }}
            />
          )}

          {/* Finished state */}
          {finished && (
            <Box
              onClick={handleFinishedClick}
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                animation: 'pulseFinished 0.8s ease-in-out infinite',
                '@keyframes pulseFinished': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.03)' },
                },
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="white">
                Готово!
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.85)" sx={{ mt: 1 }}>
                Време е за следващия сет
              </Typography>
            </Box>
          )}

          {/* Running state - big circular timer */}
          {running && (
            <Box sx={{ py: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={progress}
                  size={circleSize}
                  thickness={5}
                  sx={{
                    color: 'primary.main',
                    '& .MuiCircularProgress-circle': {
                      transition: 'stroke-dashoffset 1s linear',
                    },
                  }}
                />
                {/* Background track */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={circleSize}
                  thickness={5}
                  sx={{
                    color: 'action.hover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: -1,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatTime(remaining)}
                  </Typography>
                  {paused && (
                    <Typography variant="caption" color="warning.main" fontWeight="bold">
                      ПАУЗА
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Control buttons */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2 }}
                width="100%"
                sx={{ px: { xs: 2, sm: 0 } }}
              >
                {paused ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayIcon />}
                    onClick={resumeTimer}
                    sx={{ borderRadius: 2, px: 3, py: 1, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Продължи
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<PauseIcon />}
                    onClick={pauseTimer}
                    sx={{ borderRadius: 2, px: 3, py: 1, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Пауза
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={stopTimer}
                  sx={{ borderRadius: 2, px: 3, py: 1, width: { xs: '100%', sm: 'auto' } }}
                >
                  Спри
                </Button>
              </Stack>
            </Box>
          )}

          {/* Idle expanded state - duration picker */}
          {!isActive && expanded && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  <TimerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Почивка
                </Typography>
                <Button
                  size="small"
                  onClick={() => setExpanded(false)}
                  sx={{ minWidth: 'auto', color: 'text.secondary' }}
                  aria-label="Затвори"
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                {REST_DURATIONS.map((d) => (
                  <Button
                    key={d.seconds}
                    variant="outlined"
                    size="large"
                    onClick={() => startTimer(d.seconds)}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 2, sm: 3 },
                      fontSize: { xs: '0.95rem', sm: '1.1rem' },
                      borderRadius: 2,
                      flex: '1 1 auto',
                      minWidth: 80,
                    }}
                  >
                    {d.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RestTimer;
